import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { FaMinus, FaPlus } from "react-icons/fa";
import {
  decreaseAdults,
  decreaseChildren,
  increaseAdults,
  increaseChildren,
  setChildAge,
} from "../../_actions/searchActions";
import { DEFAULT_ADULT_AGE } from "../../helper/guests";
import { translateCount } from "../../helper/plural";

const QuantityButton = ({ kind, label, disabled, onClick }) => {
  const activate = () => {
    if (!disabled) onClick();
  };

  return (
    <div
      role='button'
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-disabled={disabled}
      className={`quantity-button quantity-${kind} d-flex align-items-center justify-content-center ${
        disabled ? "quantity-button--disabled" : ""
      }`}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activate();
        }
      }}
    >
      {kind === "plus" ? <FaPlus aria-hidden='true' /> : <FaMinus aria-hidden='true' />}
    </div>
  );
};

const CountRow = ({ label, description, value, min, max, locked, onIncrease, onDecrease, quantityClassName }) => (
  <div className='formRow'>
    <div className='labelWrap'>
      <span className='label'>{label}</span>
      {description && <span className='description'>{description}</span>}
    </div>
    <div className={`inputWrap ${quantityClassName}`}>
      <div className='quantity'>
        <input type='number' value={value} readOnly aria-label={label} tabIndex={-1} />
        <div className='quantity-nav'>
          <QuantityButton
            kind='plus'
            label={`${label} +`}
            disabled={locked || (max !== null && value >= max)}
            onClick={onIncrease}
          />
          <QuantityButton
            kind='minus'
            label={`${label} -`}
            disabled={locked || value <= min}
            onClick={onDecrease}
          />
        </div>
      </div>
    </div>
  </div>
);

// Adults and children with the age of every child, used in the search bar
// and in the booking card. Limits come from the settings of the property.
const GuestFields = ({
  persons,
  limits = {},
  locked = false,
  showErrors = false,
  focusToken = 0,
  quantityClassName = "",
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const ref = useRef(null);
  const adultAge = limits.adultAge ?? DEFAULT_ADULT_AGE;
  const years = (count) => translateCount(t, language, "years_count", count);
  const children = persons.children ?? [];

  // Moves to the first child whose age is missing after a failed search.
  useEffect(() => {
    if (!focusToken) return;
    ref.current?.querySelector(".guest-age--invalid select")?.focus();
  }, [focusToken]);

  return (
    <div className='guest-fields' ref={ref}>
      <CountRow
        label={t("adult")}
        description={t("adults_age_range", { years: years(adultAge), defaultValue: "{{years}} or older" })}
        value={persons.adults}
        min={1}
        max={limits.maxAdults ?? null}
        locked={locked}
        quantityClassName={quantityClassName}
        onIncrease={() => dispatch(increaseAdults())}
        onDecrease={() => dispatch(decreaseAdults())}
      />
      <CountRow
        label={t("children")}
        description={t("children_age_range", { years: years(adultAge - 1), defaultValue: "0-{{years}}" })}
        value={children.length}
        min={0}
        max={limits.maxChildren ?? null}
        locked={locked}
        quantityClassName={quantityClassName}
        onIncrease={() => dispatch(increaseChildren())}
        onDecrease={() => dispatch(decreaseChildren())}
      />
      {children.length > 0 && (
        <div className='guest-ages'>
          {children.map((child, index) => {
            const invalid = showErrors && !Number.isInteger(child.age);
            const id = `guest-age-${child.id}`;

            return (
              <div key={child.id} className={`guest-age ${invalid ? "guest-age--invalid" : ""}`}>
                <label htmlFor={id}>
                  {t("child_age_label", { number: index + 1, defaultValue: "Age of child {{number}}" })}
                </label>
                <select
                  id={id}
                  className='formControl'
                  value={Number.isInteger(child.age) ? child.age : ""}
                  disabled={locked}
                  aria-invalid={invalid}
                  aria-describedby={invalid ? `${id}-error` : undefined}
                  onChange={(event) =>
                    dispatch(setChildAge(child.id, event.target.value === "" ? null : Number(event.target.value)))
                  }
                >
                  <option value=''>{t("select_age", "Select age")}</option>
                  {Array.from({ length: adultAge }, (_, age) => (
                    <option key={age} value={age}>
                      {years(age)}
                    </option>
                  ))}
                </select>
                {invalid && (
                  <span id={`${id}-error`} className='guest-age__error' role='alert'>
                    {t("child_age_required", "Select the age of the child.")}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GuestFields;
